import { useJSON } from './useJSON'

export interface UsePageData {
  savePageData: <T>(pageCode: PageCode, feature: Feature, data: T) => void
  savePageDataDry: <T>(pageCode: PageCode, feature: Feature, data: T) => void
  loadPageData: (pageCode: PageCode, feature?: Feature) => { [property: string]: any }
  removePageData: (pageCode: PageCode, feature?: Feature, property?: string) => void
}

export enum PageCode {
  /** @description サイドバー */
  SIDEBAR = 'CO02501',
  /** @description 送信済フォルダ */
  SEND_FOLDER = 'TR00901',
  /** @description 受信フォルダ */
  RECEIVED_FOLDER = 'TR01001',
  /** @description 取引詳細 */
  TRANSACTION_DETAIL = 'TR01501',
  /** @description 商品一覧 */
  PRODUCT_LIST = 'PR01701',
  /** @description 商品詳細 */
  PRODUCT_DETAIL = 'PR01801',
  /** @description ドキュメント詳細 */
  DOCUMENT_DETAIL = 'DC01901',
  /** @description 取引履歴 */
  TRANSACTION_HISTORY = 'TR01601',
  /** @description 送信カート */
  SEND_CART = 'TR01101',
  /** @description 利用者一覧 */
  USER_INFO_LIST = 'SY00501',
  /** @description 一括改版送信一覧 */
  BULK_REVISION_SEND_LIST = 'BR00001',
  /** @description 改版送信編集 */
  REVISION_SEND_EDIT = 'TR01102',
  /** @description リマインドメール送信一覧 */
  REMIND_MAIL_SEND_LIST = 'TR00902',
  /** @description 公開ドキュメント一覧 */
  DIRECT_SEND_LIST = 'DR00001',
}

export enum Feature {
  /** @description 初期化 */
  INITIALIZATION = 'initialization',
  /** @description 遷移時 */
  TRANSFER = 'transfer',
  /** @description 遷移前の画面用データ */
  FROM = 'from',
  /** @description 遷移後の画面用データ */
  TO = 'to',
}

interface SaveData<T = { [property: string]: any }> {
  [feature: string]: T
}

/**
 * @description セッションストレージでページデータを扱うフック
 * @returns セッションストレージを操作するメソッドを戻す
 */
function usePageData(): UsePageData {
  const { toJSON, parseJSON } = useJSON()

  /**
   * 画面データをストレージに保存
   * @param pageCode 画面コード
   * @param feature 機能区分
   * @param data データ
   */
  const savePageData = <T = { [property: string]: any }>(
    pageCode: PageCode,
    feature: Feature,
    data: T
  ): void => {
    const oldData: SaveData = loadPageData(pageCode)

    const newData: SaveData<T & any> = {
      ...oldData,
      [feature]: { ...oldData[feature], ...data },
    }

    sessionStorage.setItem(pageCode, toJSON<SaveData<T>>(newData))
  }

  /**
   * 画面データをストレージに保存（保存中読み込み無し）
   * @param pageCode 画面コード
   * @param feature 機能区分
   * @param data データ
   */
  const savePageDataDry = <T = { [property: string]: any }>(
    pageCode: PageCode,
    feature: Feature,
    data: T
  ): void => {
    const newData: SaveData<T & any> = {
      [feature]: data,
    }

    sessionStorage.setItem(pageCode, toJSON<SaveData<T>>(newData))
  }

  /**
   * 画面データをストレージから取得
   * @param pageCode 画面コード
   * @param feature 機能区分
   * @returns データ
   */
  const loadPageData = (
    pageCode: PageCode,
    feature?: Feature
  ): { [property: string]: any } => {
    const dataJSON: string | null = sessionStorage.getItem(pageCode)

    if (!dataJSON) {
      return {}
    }

    const data = parseJSON<SaveData>(dataJSON) ?? {}

    if (!feature) {
      return data
    }

    return data[feature]
  }

  /**
   * 画面データをストレージから削除
   * @param pageCode 画面コード
   * @param feature 機能区分
   * @param property データ属性
   */
  const removePageData = (
    pageCode: PageCode,
    feature?: Feature,
    property?: string
  ): void => {
    if (!feature) {
      sessionStorage.removeItem(pageCode)
      return
    }

    const oldData: SaveData = loadPageData(pageCode, feature)

    if (!property) {
      oldData[feature] = undefined as unknown as any
      savePageData(pageCode, feature, oldData)
      return
    }

    if (oldData[feature]?.[property]) {
      oldData[feature][property] = undefined
    }

    savePageData(pageCode, feature, oldData)
  }

  return { savePageData, savePageDataDry, loadPageData, removePageData }
}

export default usePageData
